import { Link } from "react-router-dom";
import { useLanguage } from "@/lib/i18n";
import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin } from "lucide-react";

export const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer data-testid="footer" className="bg-slate-50 border-t border-slate-200">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-10 md:py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-start">
          {/* Brand */}
          <div className="lg:col-span-1 flex items-start mb-4 md:mb-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_pruva-wellness/artifacts/vnrhurmy_PRUVA%20%283%29.svg" 
              alt="Pruva Jacuzzi" 
              className="w-20 md:w-auto md:h-24 md:origin-top-left md:scale-[2]" 
            />
          </div>

          {/* Quick Links */}
          <div className="flex flex-col items-start">
            <h4 className="text-sm uppercase tracking-widest font-medium text-slate-800 mb-4">{t("footer.quickLinks")}</h4>
            <div className="space-y-2">
              {[
                { to: "/about", label: t("nav.about") },
                { to: "/engineering", label: t("nav.engineering") },
                { to: "/custom-solutions", label: t("nav.customSolutions") },
                { to: "/custom-solutions/developer-services", label: t("footer.developerServices") },
                { to: "/blog", label: t("nav.blog") },
                { to: "/contact", label: t("nav.contact") },
              ].map(link => (
                <Link key={link.to} to={link.to} className="block text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Products */}
          <div className="flex flex-col items-start">
            <h4 className="text-sm uppercase tracking-widest font-medium text-slate-800 mb-4">{t("footer.productRange")}</h4>
            <div className="space-y-2">
              <Link to="/spa-series" className="block text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200">
                {t("nav.spaSeries")}
              </Link>
              <Link to="/jacuzzi-series" className="block text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200">
                {t("nav.jacuzziSeries")}
              </Link>
            </div>
          </div>

          {/* Contact */}
          <div className="flex flex-col items-start">
            <h4 className="text-sm uppercase tracking-widest font-medium text-slate-800 mb-4">{t("footer.getInTouch")}</h4>
            <div className="space-y-3">
              <a href="tel:+971542184086" className="flex items-start gap-3 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200">
                <Phone className="w-4 h-4 mt-0.5 shrink-0" />
                +971 54 218 4086
              </a>
              <a href="mailto:info@pruvajacuzzi.com" className="flex items-start gap-3 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200">
                <Mail className="w-4 h-4 mt-0.5 shrink-0" />
                info@pruvajacuzzi.com
              </a>
              <div className="flex items-start gap-3 text-sm text-slate-500">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                {t("contact.location")}
              </div>
            </div>

            {/* Social */}
            <div className="flex items-center gap-4 mt-4">
              <a href="https://www.instagram.com/pruvajacuzzi/" target="_blank" rel="noopener noreferrer" data-testid="social-instagram" className="text-slate-400 hover:text-slate-800 transition-colors duration-200">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/profile.php?id=61587053322596" target="_blank" rel="noopener noreferrer" data-testid="social-facebook" className="text-slate-400 hover:text-slate-800 transition-colors duration-200">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.linkedin.com/company/pruva-acrylic-swimming-pools/" target="_blank" rel="noopener noreferrer" data-testid="social-linkedin" className="text-slate-400 hover:text-slate-800 transition-colors duration-200">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-200">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-4 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-400">&copy; {new Date().getFullYear()} Pruva Jacuzzi. {t("footer.rights")}</p>
          <a href="https://www.pruvajacuzzi.com" className="text-xs text-slate-400 hover:text-slate-800 transition-colors duration-200">
            www.pruvajacuzzi.com
          </a>
        </div>
      </div>
    </footer>
  );
};
